import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * Wraps a route so only authorized users can view it.
 *
 * Redirect targets are slug-aware — when the user is inside a
 * `/c/[slug]/*` client environment we keep them inside it:
 *   - Unauthenticated visitors → `/[slug]` (bare-slug branded login page,
 *     the same page a QR-code scan resolves to). Preserves branding.
 *   - Permission-denied visitors (wrong role) → `/c/[slug]` root, so they
 *     land back on the client dashboard rather than being ejected to the
 *     platform-wide home.
 * Outside a client env the redirects fall back to the platform-wide
 * `/login` and `/` respectively.
 */
export function ProtectedRoute({ children, requireAdmin = false, requireEditor = false, requireClient = false }) {
  const { user, bootstrapped } = useAuth();
  const { pathname } = useLocation();
  const slugMatch = pathname.match(/^\/c\/([^/]+)(?:\/|$)/);
  const slug = slugMatch ? slugMatch[1] : null;
  const loginTarget = slug ? `/${slug}` : "/login";
  const homeTarget = slug ? `/c/${slug}` : "/";

  if (!bootstrapped) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-sm uppercase tracking-[0.2em] text-zinc-500">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to={loginTarget} replace />;
  if (requireAdmin && user.role !== "admin") return <Navigate to={homeTarget} replace />;
  if (requireEditor && user.role !== "admin" && !user.is_editor) return <Navigate to={homeTarget} replace />;
  // iter88 — /c/[slug]/editor access. Allowed for:
  //   - role="client"  (native brewery/pub owner)
  //   - role="admin"   (Super-Admin — impersonation via ?as_client=slug)
  //   - is_editor      (platform Editor — content operators who need to
  //                     save changes for ANY pub; backend `_require_client`
  //                     matches this same shape).
  if (
    requireClient &&
    user.role !== "client" &&
    user.role !== "admin" &&
    !user.is_editor
  ) return <Navigate to={homeTarget} replace />;
  // iter99 — master-portal lockdown. Every authenticated route on the
  // root domain (no `/c/[slug]/` prefix) is now Super-Admin only. Non-
  // admins get bounced back to `/`, where LoginPage renders the
  // "SUPER-ADMINS ONLY" red banner + a "Not a Super-Admin? Enter your
  // pub URL →" self-service helper. Platform editors (is_editor=true,
  // no client_slug) still need `/editor` on master to do their job —
  // their route sets `requireEditor` and opts out of this lockdown.
  if (!slug && user.role !== "admin" && !requireEditor) {
    return <Navigate to="/" replace />;
  }
  return children;
}
